
export  function getCursorContext() {
    let cursorTextNodeAndParent = getTextNodeAndParent();
    if (!cursorTextNodeAndParent) return null;

    // we now normalize the textNodeParent to make sure there's only one text node
    // makes subsequent code a lot simpler
    cursorTextNodeAndParent.textNodeParent.normalize();
    cursorTextNodeAndParent = getTextNodeAndParent();
    if (!cursorTextNodeAndParent) return null;

    const { textNode, textNodeParent, selection } = cursorTextNodeAndParent;

    const range = selection.getRangeAt(0);
    const textNodeContent = textNode.textContent;
    const cursorCharacterPosition = range.startOffset;
    if (!(cursorCharacterPosition >= 0)) return null;

    return {
        textNode, textNodeParent, textNodeContent, cursorCharacterPosition,
        textBeforeCursor: textNodeContent.substring(0, cursorCharacterPosition),
        textAfterCursor:  textNodeContent.substr(cursorCharacterPosition)
    };
}

export function containByScreen(element, anchorPoint, options) {
    if (window.getComputedStyle) {
        const style = window.getComputedStyle(element);
        if (style.position !== 'fixed') {
            // eslint-disable-next-line no-console
            console.error('containByScreen only works on fixed position elements', element);
        }
    }

    const elRect = getBoundingClientRect(element);
    const anchorRect = getBoundingClientRect(anchorPoint);

    const buffers = {
        all:    options.buffer || 0,
        top:    options.topBuffer || 5,
        bottom: options.bottomBuffer || 0,
        left:   options.leftBuffer || 0,
        right:  options.rightBuffer || 0
    };

    const optionPositions = Array.isArray(options.position) ? options.position : [options.position].filter(Boolean);
    const optionHAligns = Array.isArray(options.hAlign) ? options.hAlign : [options.hAlign].filter(Boolean);
    const optionVAligns = Array.isArray(options.vAlign) ? options.vAlign : [options.vAlign].filter(Boolean);

    const positions = optionPositions.length > 0 && options.forcePosition ?
        optionPositions :
        [...new Set(optionPositions.concat(['top', 'bottom', 'left', 'right']))];
    const hAligns = optionHAligns.length > 0 && options.forceHAlign ?
        optionHAligns :
        [...new Set(optionHAligns.concat(['center', 'left', 'right']))];
    const vAligns = optionVAligns.length > 0 && options.forceVAlign ?
        optionVAligns :
        [...new Set(optionVAligns.concat(['center', 'top', 'bottom']))];
    const allPossibleChoices = positions.map(position =>
        (position === 'cover') ?
            hAligns.map(hAlign => vAligns.map(vAlign => ({ position, hAlign, vAlign }))).flat() :
            (position === 'top' || position === 'bottom') ?
                hAligns.map(hAlign => ({ position, hAlign, vAlign: 'center' })) :
                vAligns.map(vAlign => ({ position, hAlign: 'center', vAlign }))
    ).flat();

    let choiceAndCoord = null;
    for (let i = 0; i < allPossibleChoices.length; i++) {
        const choice = allPossibleChoices[i];
        const coord = positionAndAlign(elRect, anchorRect, choice, buffers);
        const { top, left } = coord;
        if (
            top - buffers.all - buffers.top >= 0 &&
            left - buffers.all - buffers.left >= 0 &&
            top + elRect.height + buffers.all + buffers.bottom <= window.innerHeight &&
            left + elRect.width + buffers.all + buffers.right <= window.innerWidth
        ) {
            choiceAndCoord = { choice, coord };
            break;
        }
    }

    // Fallback if we failed to find a position that fit on the screen.
    if (!choiceAndCoord) {
        const choice = {
            position: optionPositions[0] || 'top',
            hAlign:   optionHAligns[0] || 'center',
            vAlign:   optionVAligns[0] || 'center'
        };
        choiceAndCoord = {
            choice,
            coord: positionAndAlign(elRect, anchorRect, choice, buffers)
        };
    }

    element.style.top = `${choiceAndCoord.coord.top}px`;
    element.style.left = `${choiceAndCoord.coord.left}px`;

    return choiceAndCoord.choice;
}

function getTextNodeAndParent() {
    const selection = document.getSelection();
    if (!selection) return null;

    const textNode = selection.anchorNode;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return null;

    const textNodeParent = textNode.parentNode;
    if (!textNodeParent) return null;

    return { textNode, textNodeParent, selection };
}



function getBoundingClientRect(el) {
    let rect = el.getBoundingClientRect();
    if (!('width' in rect)) {
        // IE <9 support
        rect = Object.assign(({
            width:  rect.right - rect.left,
            height: rect.bottom - rect.top
        }), rect);
    }
    return rect;
}

function positionAndAlign(elRect, anchorRect, { position, hAlign, vAlign }, buffers) {
    let top = 0, left = 0;
    if (position === 'cover') {
        switch (hAlign) {
            case 'center':
                left = Math.round((anchorRect.left + anchorRect.right - elRect.width) / 2);
                break;
            case 'left':
                left = Math.floor(anchorRect.left);
                break;
            case 'right':
                left = Math.ceil(anchorRect.right - elRect.width);
                break;
            default: throw new Error('Should not happen');
        }
        switch (vAlign) {
            case 'center':
                top = Math.round((anchorRect.top + anchorRect.bottom - elRect.height) / 2);
                break;
            case 'top':
                top = Math.floor(anchorRect.top);
                break;
            case 'bottom':
                top = Math.ceil(anchorRect.bottom - elRect.height);
                break;
            default: throw new Error('Should not happen');
        }
    } else if (position === 'top' || position === 'bottom') {
        switch (position) {
            case 'top':
                top = Math.floor(anchorRect.top - elRect.height - buffers.all - buffers.bottom);
                break;
            case 'bottom':
                top = Math.ceil(anchorRect.bottom + buffers.all + buffers.top);
                break;
            default: throw new Error('Should not happen');
        }
        switch (hAlign) {
            case 'center':
                left = Math.round((anchorRect.left + anchorRect.right - elRect.width) / 2);
                break;
            case 'left':
                left = Math.round(anchorRect.left);
                break;
            case 'right':
                left = Math.round(anchorRect.right - elRect.width);
                break;
            default: throw new Error('Should not happen');
        }
    } else {
        switch (position) {
            case 'left':
                left = Math.floor(anchorRect.left - elRect.width - buffers.all - buffers.right);
                break;
            case 'right':
                left = Math.ceil(anchorRect.right + buffers.all + buffers.left);
                break;
            default: throw new Error('Should not happen');
        }
        switch (vAlign) {
            case 'center':
                top = Math.round((anchorRect.top + anchorRect.bottom - elRect.height) / 2);
                break;
            case 'top':
                top = Math.round(anchorRect.top);
                break;
            case 'bottom':
                top = Math.round(anchorRect.bottom - elRect.height);
                break;
            default: throw new Error('Should not happen');
        }
    }
    return { top, left };
}

